import { Injectable, OnDestroy, inject } from "@angular/core";
import { AccessLevel, ApplicationDataType, ControlPath, ControlType, DataContainer, DateControl, IApplicationDataObject, LikertControl, Qualifier, RepeaterControl, iApplyFormControl, isApplicationDataObject, isControlWithValidators, isValueControl } from "app/shared/model";
import { BehaviorSubject, Observable, Subject, finalize, map, of } from "rxjs";
import { ControlErrors, FindControlFn, mergeControlErrors, ValidateControlFn, ValidationHelpers, ValidationResult } from "./validation-shared";
import { ControlHelperService } from "../../control-helper.service";
import { hasValue, isObject, toJson } from "app/shared";
import { ApplicationHelperService } from "../../application-helper.service";
import { ControlVisibilityService } from "app/modules/visibility/control-visibility.service";
import { ApplicationsServiceProxy } from "app/shared/services/application-service.proxy";
import { DataSharingService } from "app/shared/services/data-sharing.service";
import { IValidateApplicationResponse, ValidateApplicationResponse } from "app/shared/services/model/application-service.model";
import { GlobalService } from "app/shared/services/global.service";
import { AlertService } from "@full-fledged/alerts";
import { DateTime } from "luxon";
import { TimeHelper } from "app/shared/helpers/time-helper";

type PendingServerValidationRedirect = {
    result: ValidationResult;
    pageId: number | null;
    subPageId: number | null;
    token: number;
    applied: boolean;
};

@Injectable()
export class ValidationService implements OnDestroy {
    private readonly _applicationHelperService = inject(ApplicationHelperService);
    private readonly _controlHelperService = inject(ControlHelperService);
    // private readonly _ruleEvaluatorService = inject(RuleEvaluatorService);
    private readonly _controlVisibilityService = inject(ControlVisibilityService);
    private readonly _applicationServiceProxy = inject(ApplicationsServiceProxy);
    private readonly _dataSharingService = inject(DataSharingService);
    private readonly _globalService = inject(GlobalService);
    private readonly _alertService = inject(AlertService);
    private readonly _unsubscribeAll: Subject<any> = new Subject<any>();
    private readonly _validationResult: BehaviorSubject<ValidationResult> = new BehaviorSubject(null);
    private _pendingServerValidationRedirect: PendingServerValidationRedirect | null = null;
    private _pendingServerValidationRedirectToken = 0;
    private _pendingServerValidationRedirectTimer: ReturnType<typeof setTimeout> | null = null;

    // TODO: Offline Detection
    isOnline: boolean = true;

    get validationResult$(): Observable<ValidationResult> {
        return this._validationResult.asObservable();
    }

    set validationResult(result: ValidationResult) {
        this._validationResult.next(result);
    }

    ngOnDestroy(): void {
        this.cancelPendingServerValidationRedirect();
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    initializeFromServerResult(serverResult: IValidateApplicationResponse) {
        this.clearErrors(true);
        if (hasValue(serverResult.errors)) {
            let validationResult = <ValidationResult>{
                success: false,
                valid: false,
                firstPage: serverResult.firstPage,
                errors: serverResult.errors // as ControlErrors
            };

            this._validationResult.next(validationResult);
        }
    }

    queueServerValidationRedirect(serverResult: ValidationResult, pageId?: number | null, subPageId?: number | null): void {
        this.clearPendingServerValidationRedirectTimer();
        this._pendingServerValidationRedirect = {
            result: {
                ...serverResult,
                success: false,
                valid: false,
            },
            pageId: pageId ?? null,
            subPageId: subPageId ?? null,
            token: ++this._pendingServerValidationRedirectToken,
            applied: false,
        };
    }

    applyPendingServerValidationForPage(pageId: number, subPageId?: number | null): boolean {
        const pending = this._pendingServerValidationRedirect;
        if (!pending) {
            return false;
        }

        if (!this.isPendingServerValidationRedirectTarget(pending, pageId, subPageId)) {
            return true;
        }

        if (!pending.applied && hasValue(pending.result.errors)) {
            this._validationResult.next({
                ...pending.result,
                errors: pending.result.errors,
                valid: false,
            });
            pending.applied = true;
        }

        this.schedulePendingServerValidationRedirectRelease(pending.token, 1000);
        return true;
    }

    completePendingServerValidationRedirectForPage(pageId: number, subPageId?: number | null): void {
        const pending = this._pendingServerValidationRedirect;
        if (!pending || !pending.applied || !this.isPendingServerValidationRedirectTarget(pending, pageId, subPageId)) {
            return;
        }

        this.schedulePendingServerValidationRedirectRelease(pending.token, 0);
    }

    clearErrors(force = false) {
        if (!force && this._pendingServerValidationRedirect) {
            return;
        }

        this._validationResult.next(null);
    }

    clearErrorsForControl(controlName: string, qualifier: Qualifier) {
        const validationResult = this._validationResult.getValue();
        if (!validationResult?.errors) {
            return;
        }

        const nextErrors = { ...validationResult.errors };
        let isErrorCleared = false;

        if (Object.hasOwn(nextErrors, controlName)) {
            delete nextErrors[controlName];
            isErrorCleared = true;
        }

        const prunedErrors = ValidationService.pruneEmptyErrors(nextErrors);
        if (!ValidationService.hasErrors(prunedErrors)) {
            this.clearErrors(true);
            return;
        }

        if (isErrorCleared || qualifier?.length) {
            this._validationResult.next({
                ...validationResult,
                errors: prunedErrors,
                valid: false,
            });
        }
    }

    private isPendingServerValidationRedirectTarget(pending: PendingServerValidationRedirect, pageId: number, subPageId?: number | null): boolean {
        if (pending.pageId == null) {
            return true;
        }

        return pending.pageId === pageId &&
            (pending.subPageId == null || pending.subPageId === (subPageId ?? null));
    }

    private schedulePendingServerValidationRedirectRelease(token: number, delayMs: number): void {
        this.clearPendingServerValidationRedirectTimer();
        this._pendingServerValidationRedirectTimer = setTimeout(() => {
            if (this._pendingServerValidationRedirect?.token === token) {
                this.releasePendingServerValidationRedirect();
            }
        }, delayMs);
    }

    private releasePendingServerValidationRedirect(): void {
        this.clearPendingServerValidationRedirectTimer();
        this._pendingServerValidationRedirect = null;
    }

    private cancelPendingServerValidationRedirect(): void {
        this.releasePendingServerValidationRedirect();
    }

    private clearPendingServerValidationRedirectTimer(): void {
        if (this._pendingServerValidationRedirectTimer) {
            clearTimeout(this._pendingServerValidationRedirectTimer);
            this._pendingServerValidationRedirectTimer = null;
        }
    }

    validateSingleControl(control: iApplyFormControl, applicationData: DataContainer, qualifier?: Qualifier): ValidationResult
    {
        const currentResult = this._validationResult.getValue()
            ?? <ValidationResult>{
                success: false,
                valid: false,
                errors: <ControlErrors>{}
            };
        const controlList = this._dataSharingService.controls?.length
            ? this._dataSharingService.controls
            : [control];
        const errors = <ControlErrors>{};
        const matchControl = (candidate: iApplyFormControl): boolean => {
            if (candidate.id && control.id) {
                return candidate.id === control.id;
            }

            return candidate.name === control.name && candidate.parentControlId === control.parentControlId;
        };

        this.validateControls(
            controlList,
            applicationData,
            candidate => matchControl(candidate) && ValidationHelpers.isMandatory(candidate),
            ValidationService.validateMandatory,
            errors,
            undefined,
            qualifier,
        );

        this.validateControls(
            controlList,
            applicationData,
            candidate => matchControl(candidate) && ValidationHelpers.hasValidators(candidate),
            ValidationService.validateRegex,
            errors,
            undefined,
            qualifier,
        );

        this.validateControls(
            controlList,
            applicationData,
            candidate => matchControl(candidate) && candidate.type == ControlType.Datepicker,
            ValidationService.validateDate,
            errors,
            undefined,
            qualifier,
        );

        this.validateControls(
            controlList,
            applicationData,
            candidate => matchControl(candidate) && candidate.type == ControlType.Timepicker,
            ValidationService.validateDate,
            errors,
            undefined,
            qualifier,
        );

        this.validateControls(
            controlList,
            applicationData,
            candidate => matchControl(candidate) && candidate.type == ControlType.Repeater,
            ValidationService.validateUniqueness,
            errors,
            undefined,
            qualifier,
        );

        const result = <ValidationResult>{
            ...currentResult,
            errors: ValidationService.removeControlErrors(
                ValidationService.pruneEmptyErrors(currentResult.errors),
                ValidationService.getControlErrorKeys(control, qualifier),
            )
        };

        result.errors = ValidationService.pruneEmptyErrors(mergeControlErrors(result.errors, errors));
        result.valid = !ValidationService.hasErrors(result.errors);
        result.success = true;

        this._validationResult.next(result);

        // if (!result.valid) {
        //     this._validationResult.next(result);
        //     this.HandleValidationFailedEffect();
        // }

        return result;
    }

    validateApplication(allControls: iApplyFormControl[], applicationData: DataContainer, pageId: number, subPageId?: number, index?: number): Observable<ValidationResult> {
        let result = this.validateApplicationLocal(allControls, applicationData, pageId, subPageId, index);

        if (!result.valid) {
            return of(result);
        }

        return this.validateApplicationServerSide(applicationData, pageId, subPageId);
    }

    validateApplicationLocal(allControls: iApplyFormControl[], applicationData: DataContainer, pageId: number, subPageId?: number, index?: number): ValidationResult {
        let result = <ValidationResult>{
            success: false,
            valid: false,
            errors: <ControlErrors>{}
        };

        // TODO!: If specified, and the subPageId is the LAST sub-page group control on the parent page
        //!       then we need to validate all the (visible / accessible) sub-page group controls on the parent page.
        let controls = (subPageId > 0)
            ? allControls.filter(control => control.id === subPageId)
            : allControls.filter(control => control.pageId == pageId || 0);

        result.errors = this.validateAll(controls, applicationData, index);
        result.valid = Object.keys(result.errors).length === 0;
        result.success = true;

        if (!result.valid) {
            this._validationResult.next(result);
            this.HandleValidationFailedEffect();
        }

        return result;
    }

    validateApplicationServerSide(applicationData: DataContainer, pageId: number, subPageId?: number): Observable<ValidationResult> {
        const formVersion: number = this._dataSharingService.application?.useFormVersion
            ? this._dataSharingService.application.formVersion
            : null;

        const input = {
            formId: this._dataSharingService.formId,
            formVersion: formVersion,
            applicationId: this._dataSharingService.applicationId,
            data: toJson(applicationData),
            pageId: pageId ?? this._dataSharingService?.currentPage?.pageId ?? 0,
            subpageControlId: !subPageId ? null : subPageId
        };

        if (this._globalService.isOffline()) {
            this._globalService.hideModal('');

            var res = <ValidationResult>{
                success: true,
                valid: true,
                errors: {}
            }
            this._validationResult.next(res);
            return of(res);
        }

        this._globalService.showModal('validateApplication', '');

        return this._applicationServiceProxy.validateApplication(input)
            .pipe(
                map((response: ValidateApplicationResponse | undefined) => {
                    this._globalService.hideModal('');
                    const result = <ValidationResult>{
                        success: response.success,
                        valid: response.valid,
                        errors: response.errors ?? {}
                    };

                    // result.valid = Object.keys(result.errors).length === 0;
                    this._validationResult.next(result);
                    if (!result.valid) {
                        this.HandleValidationFailedEffect();
                    }
                    return result;
                }),
                finalize(() => this._globalService.hideModal('validateApplication')),
            );

    }

    validateAll(controls: iApplyFormControl[], data: DataContainer, repeaterIndex: number): ControlErrors {
        let errors = <ControlErrors>{};
        this.validateControls(controls, data, ValidationHelpers.isMandatory, ValidationService.validateMandatory, errors, repeaterIndex, null);
        this.validateControls(controls, data, ValidationHelpers.hasValidators, ValidationService.validateRegex, errors, repeaterIndex, null);
        this.validateControls(controls, data, (control) => control.type == ControlType.Datepicker, ValidationService.validateDate, errors, repeaterIndex, null);
        this.validateControls(controls, data, (control) => control.type == ControlType.Timepicker, ValidationService.validateTime, errors, repeaterIndex, null);
        this.validateControls(controls, data, (control) => control.type == ControlType.Repeater, ValidationService.validateUniqueness, errors, repeaterIndex, null);
        return errors;
    }

    HandleValidationFailedEffect(scrollDom?: HTMLElement) {
        if (!scrollDom) {
            scrollDom = document.querySelector('application-page-view .mat-mdc-tab-body-active .mat-mdc-tab-body-content') as HTMLElement | undefined;
            this._alertService.danger('Please correct the validation errors present on this page');
        }

        scrollDom?.scrollTo(0, 0);
    }

    private static addError(fullControlName: string, message: string, errors: ControlErrors) {
        errors = errors || {};
        let controlErrors = errors[fullControlName];
        if (!Array.isArray(controlErrors)) {
            controlErrors = errors[fullControlName] = [];
        }

        if (controlErrors.indexOf(message) === -1) {
            controlErrors.push(message);
        }

        return errors;
    }

    private validateControls(
        controlList: iApplyFormControl[],
        data: DataContainer,
        findCtrlFn: FindControlFn,
        validatorFn: ValidateControlFn,
        errors: ControlErrors,
        repeaterIndex: number,
        qualifier: Qualifier
    ): ControlErrors {
        const controlsToValidate = this._controlHelperService.findAll(controlList, findCtrlFn);

        for (const control of controlsToValidate) {
            let repeater = this._controlHelperService.findNearestRepeater(controlList, control);
            if (repeater) {
                if (!data[repeater.name]) {
                    continue;
                }

                if (hasValue(repeaterIndex) || qualifier?.length > 0) {
                    // currentPath where repeaterName is in the path
                    let repeaterPath = qualifier?.findPath(repeater.name) ?? new ControlPath(repeater.name, repeaterIndex);

                    // Validate the control on a specific row (in repeater page for example)
                    this.validateSetup(controlList, control, data, errors, validatorFn, repeaterPath, qualifier);
                } else {
                    for (let rowIndex = 0; rowIndex < data[repeater.name].length; rowIndex++) {
                        // Validate the same control for each row of the repeater...
                        let repeaterPath = new ControlPath(repeater.name, rowIndex)
                        this.validateSetup(controlList, control, data, errors, validatorFn, repeaterPath, qualifier);
                    }
                }
            } else {
                this.validateSetup(controlList, control, data, errors, validatorFn);
            }
        }

        return errors;
    }

    private validateSetup(
        controlList: iApplyFormControl[],
        control: iApplyFormControl,
        data: DataContainer,
        errors: ControlErrors,
        validateFn: ValidateControlFn,
        repeaterPath?: ControlPath,
        qualifier?: Qualifier) {
        let fullControlName = control.name;
        let q = qualifier ? qualifier.clone() : new Qualifier();

        if (repeaterPath) {
            if (q.length === 0) {
                q.push(repeaterPath);
            }

            fullControlName = q.safeControlName(control.name);
        }

        // need to check for visibility as repeater's subcontrols are deeply copied for each row, lastIsVisibleValue may not be 100% correct
        let isVisible = this._controlVisibilityService.isVisible(control, data, q, controlList, null);
        if (control.access === AccessLevel.Write && isVisible && control.lastIsVisibleValue !== false) {
            if (this.isOnline === false && control.type === ControlType.Filebrowser) {
                return errors;
            }

            const val = this._applicationHelperService.findFieldValue(q, control.name, data);
            validateFn(control, fullControlName, val, errors);
        }

        return errors;
    }

    private static pruneEmptyErrors(errors?: ControlErrors): ControlErrors {
        let result = <ControlErrors>{};

        Object.entries(errors ?? {}).forEach(([key, value]) => {
            if (value?.length > 0) {
                result[key] = value;
            }
        });

        return result;
    }

    private static hasErrors(errors?: ControlErrors): boolean {
        return Object.keys(ValidationService.pruneEmptyErrors(errors)).length > 0;
    }

    private static getControlErrorKeys(control: iApplyFormControl, qualifier?: Qualifier): string[] {
        const keys = new Set<string>([control.name]);

        if (qualifier?.length) {
            keys.add(qualifier.safeControlName(control.name));
        }

        return [...keys];
    }

    private static removeControlErrors(errors: ControlErrors, controlKeys: string[]): ControlErrors {
        const result = { ...errors };

        controlKeys.forEach(key => {
            delete result[key];
        });

        return result;
    }

    private static normalizeDateValue(controlValue: unknown): string | null {
        if (!hasValue(controlValue)) {
            return null;
        }

        if (controlValue instanceof Date) {
            return isNaN(controlValue.getTime()) ? null : DateTime.fromJSDate(controlValue).toISODate();
        }

        if (typeof controlValue === 'object' && controlValue !== null && Object.hasOwn(controlValue, 'val')) {
            return ValidationService.normalizeDateValue((controlValue as { val?: unknown }).val);
        }

        let rawValue = controlValue;
        if (typeof rawValue === 'number') {
            rawValue = rawValue.toString();
        }

        if (typeof rawValue !== 'string') {
            return null;
        }

        const trimmedValue = rawValue.trim();
        if (!trimmedValue) {
            return null;
        }

        if (trimmedValue === '0' || trimmedValue.startsWith('+') || trimmedValue.startsWith('-')) {
            const relativeDate = new Date().convertRelativeDateString(trimmedValue);
            return relativeDate instanceof Date && !isNaN(relativeDate.getTime())
                ? DateTime.fromJSDate(relativeDate).toISODate()
                : null;
        }

        if (/^\d{4}\/\d{2}\/\d{2}$/.test(trimmedValue)) {
            const slashDate = DateTime.fromFormat(trimmedValue, 'yyyy/MM/dd');
            return slashDate.isValid ? slashDate.toISODate() : null;
        }

        if (/^\d{4}-\d{2}-\d{2}(?:T.*)?$/.test(trimmedValue)) {
            const isoDate = DateTime.fromISO(trimmedValue);
            return isoDate.isValid ? isoDate.toISODate() : null;
        }

        if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmedValue)) {
            const auDate = DateTime.fromFormat(trimmedValue, 'dd/MM/yyyy');
            return auDate.isValid ? auDate.toISODate() : null;
        }

        return null;
    }

    private static validateMandatory(control: iApplyFormControl, fullControlName: string, controlValue: any, errors: ControlErrors): void {
        // TODO: Check if control is in list of known unvalidatable control types (Credit Card for example).
        if (!control || control.access < AccessLevel.Write || control.type === ControlType.Creditcard || !isValueControl(control)) {
            return;
        }

        let message = control.mandatoryMessage?.format(controlValue, control.name, control.label);

        if (Array.isArray(controlValue) && controlValue.length === 0) {
            ValidationService.addError(fullControlName, message || "Please select at least one option for '" + (control.label || control.name) + "'", errors);
        }
        else if (control.type === ControlType.Likert) {
            for (const question of (<LikertControl>control).questions) {
                let answer: any[] = isObject(controlValue) && hasValue(controlValue[question.name])
                    ? controlValue[question.name]
                    : [];

                if (answer?.length === 0) {
                    message = control.mandatoryMessage.format(controlValue, control.name, question.label || control.label);
                    ValidationService.addError(fullControlName + "-" + question.name, message || "Please enter a value for '" + question.label + "'", errors);
                }
            }
        }
        else if (control.type === ControlType.Twofactorverification) {
            if (!hasValue(controlValue)) {
                ValidationService.addError(fullControlName, message || "Please verify the phone number.", errors);
            }
        }
        else if (!hasValue(controlValue)) {
            ValidationService.addError(fullControlName, message || "Please enter a value for '" + (control.label || control.name) + "'", errors);
        }
    }

    private static validateRegex(control: iApplyFormControl, fullControlName: string, controlValue: any, errors: ControlErrors): void {
        if (!control || control.access < AccessLevel.Write || !isControlWithValidators(control) || !isValueControl(control)) {
            return;
        }

        let messages = [];
        let enableEmptyValueValidation = false; // systemSettings?.IgnoreRegExRequired
        if (enableEmptyValueValidation || hasValue(controlValue)) {
            for (const controlValidator of control.validators) {
                // cannot pass null to regex - test for it and pass empty string instead
                const testValue = hasValue(controlValue) ? controlValue : '';
                const regex = new RegExp(controlValidator.validator.regex);

                if (regex.test(testValue)) {
                    // first PASS, we return.
                    return;
                } else {
                    messages.push(controlValidator.message.format(testValue, control.name, control.label));
                }
            }

            if (messages.length > 0) {
                let message = messages.join(', or ');
                ValidationService.addError(fullControlName, message, errors);
            }
        }
    }

    private static validateTime(controls: iApplyFormControl, fullControlName: string, controlValue: any, errors: ControlErrors) {
        if (hasValue(controlValue) && !TimeHelper.parse(controlValue)) {
            ValidationService.addError(fullControlName, TimeHelper.formatError, errors);
        }
    }

    private static validateDate(control: DateControl, fullControlName: string, controlValue: any, errors: ControlErrors) {
        if (!control || control.access < AccessLevel.Write) {
            return errors;
        }

        const normalizedValue = ValidationService.normalizeDateValue(controlValue);
        if (hasValue(controlValue) && !normalizedValue) {
            let message = "Date format should be 'dd/mm/yyyy'";
            errors = ValidationService.addError(fullControlName, message, errors);
            return errors;
        }

        if (!normalizedValue) {
            return errors;
        }

        const current = DateTime.fromISO(normalizedValue);

        if (control.minDate && control.maxDate) {
            const minDate = DateTime.fromISO(new Date().convertRelativeDateString(control.minDate).toISOString());
            const maxDate = DateTime.fromISO(new Date().convertRelativeDateString(control.maxDate).toISOString());
            if (current < minDate || current > maxDate) {
                let message = `Date should be between '${minDate.toFormat('dd/MM/yyyy')}' and '${maxDate.toFormat('dd/MM/yyyy')}'`;
                errors = ValidationService.addError(fullControlName, message, errors);
            }

            return errors
        }

        if (control.minDate) {
            // const minDate = new Date().convertRelativeDateString(control.minDate);
            const minDate = DateTime.fromISO(new Date().convertRelativeDateString(control.minDate).toISOString()); // moment(minDate);
            if (current < minDate) {
                let message = `Date should be after '${minDate.toFormat('dd/MM/yyyy')}'`;
                errors = ValidationService.addError(fullControlName, message, errors);
            }
        }

        if (control.maxDate) {
            const maxDate = DateTime.fromISO(new Date().convertRelativeDateString(control.maxDate).toISOString()); // moment(minDate);
            if (current > maxDate) {
                let message = `Date should be before '${maxDate.toFormat('dd/MM/yyyy')}'`;
                errors = ValidationService.addError(fullControlName, message, errors);
            }
        }

        return errors;
    }

    static validateUniqueness(repeater: RepeaterControl, fullControlName: string, container: DataContainer, errors: ControlErrors, reverse: boolean = false) {
        function parseRow(repeater: RepeaterControl, repeaterRow: DataContainer, uniqueValues: string[], parentIndex: number) {
            if (Array.isArray(repeaterRow)) {
                for (let i = 0; i < repeaterRow.length; i++) {
                    parseRow(repeater, repeaterRow[i], uniqueValues, i);
                }
                return;
            }

            let uniqueValue = '';
            for (const fieldName of repeater.uniqueFields) {
                uniqueValue += fieldName + '=' + ValidationService.getCleanValue(repeaterRow[fieldName]) + ';';
            }

            if (uniqueValues.includes(uniqueValue.toLowerCase()) && uniqueValue !== '') {
                ValidationService.addError(fullControlName, message || 'Uniqueness validation failed. Please remove a duplicate row.', errors);
                for (const fieldName of repeater.uniqueFields) {
                    const fullSubControlName = fullControlName + '-' + parentIndex + '-' + fieldName;
                    ValidationService.addError(fullSubControlName, 'The value of this field must be unique', errors);
                }

                return;
            }

            uniqueValues.push(uniqueValue.toLowerCase());
        }

        if (!repeater.uniqueFields || repeater.uniqueFields.length === 0 || !container || !repeater.lastIsVisibleValue) {
            return errors;
        }

        let message = repeater.uniqueValidationMessage ? repeater.uniqueValidationMessage.format('', repeater.name, repeater.label) : null,
            uniqueValues = [];

        // If it is the first row for repeater page view, we may need to check in reverse order for uniqueness.
        if (reverse) {
            for (let i = container.length - 1; i >= 0; i--) {
                parseRow(repeater, container[i], uniqueValues, i);
            }
        } else {
            for (let i = 0; i < container.length; i++) {
                parseRow(repeater, container[i], uniqueValues, i);
            }
        }

        return errors;
    }

    private static getCleanValue(valueObj: any): string | string[] | IApplicationDataObject {
        if (isApplicationDataObject(valueObj)) {
            if (valueObj._type === ApplicationDataType.ValueArray) {
                return valueObj.values.map(obj => obj.value) ?? [];
            } else {
                return valueObj.value;
            }
        }

        return valueObj;
    }
}
